<template>
  <a
    class="product-vignette"
    :class="getTargetedPromotion ? 'product-vignette--promotion' : ''"
    :testId="`${testId}-vignette`"
    :href="product.url"
    :title="product.name"
    @click="vignetteHandleClick"
  >
    <article class="product-vignette__item">
      <div class="product-vignette__image b-lazy-placeholder qa-thematic-image-wrapper b-loaded">
        <img :alt="product.name" :src="generateNewUrl(product.gallery.items[0], 380, 285)" format="webp" />
      </div>
      <div class="product-vignette__info qa-thematic-content">
        <h4 class="product-vignette__title">{{ product.name }}</h4>
        <rating v-if="showRating" :ratingClass="product.ratingClass" :reviewsCount="product.reviewsCount" :testId="testId" />
        <div
          v-if="screenSize == 'mobile'"
          class="product-vignette__description product-vignette__clamp"
          :class="showRating ? 'product-vignette__clamp3' : 'product-vignette__clamp4'"
          :data-testid="`${testId}-vignette_description-mobile`"
        >
          {{ hasSevLocation ? getSevLocation(product, related) : product.shortDescription }}
        </div>
        <ul v-else class="product-vignette__description qa-thematic-list" :data-testid="`${testId}-vignette_description-desktop`">
          <li class="product-vignette__description-item">
            <span class="product-vignette__description-icon si si-people"></span>
            <span>{{ product.isSev ? getNumPeopleText(product.personCount) : product.shortDescription }}</span>
          </li>
          <li
            class="product-vignette__description-item"
            :data-testid="`${testId}-vignette_description-location`"
            v-if="
              !hasNoLocation && (hasSevLocation || !!product.subtitle) && !product.closest_activity_distance && !product.number_activities
            "
          >
            <span class="product-vignette__description-icon si si-marker"></span>
            <span>{{ hasSevLocation ? getSevLocation(product, related) : product.subtitle }}</span>
          </li>
        </ul>
        <slot name="extraDescription"></slot>
        <delivery-format :isFreeShipping="isFreeShipping" :isEboxDelivery="isEboxDelivery" :testId="testId" :screenSize="screenSize" />
      </div>
      <div class="product-vignette__bottom">
        <price :product="product" :discount="this.related.buyer_discount" :testId="testId" />
      </div>
    </article>
  </a>
</template>

<script lang="js">
import { productVignette } from './index.js';
import Price from './__partials/Price';
import Rating from './__partials/Rating';
import DeliveryFormat from './__partials/DeliveryFormat';
import { screenSizesMixin } from '../../utils/mixins/screenSizesMixin';
import { generateNewUrl } from '../../utils/domUtils';
import { getTargetedPromotion } from '../../utils/targetedPromotionsUtils';


export default {
  name: 'productVignette',
  components: { Price, Rating, DeliveryFormat },
  mixins: [screenSizesMixin],
  props: {
    ...productVignette.propTypes,
  },
  data() {
    return {
      screenPx: 640,
    };
  },
  computed: {
    showRating() {
      return this.product.rating !== null && this.product.reviewsCount;
    },
    isFreeShipping() {
      return (this.product.isFormatPhysical || this.product.isFormatPof) && (this.related.buyer_discount && this.related.buyer_discount.free_shipping);
    },
    isEboxDelivery() {
      return this.product.isFormatEvoucherBuyer && !this.product.isFormatPhysical && !this.product.isFormatPof;
    },
    hasSevLocation() {
      return this.product.isSev && this.getSevLocation(this.product, this.related) !== '';
    },
    hasNoLocation() {
      if (!this.product.layoutFlags || !this.product.layoutFlags.length) {
        return false;
      }

      return this.product.layoutFlags.filter(item => item.options.some(option =>
        option.value == 'hide_location')).length;
    },
    isMobile() {
      return this.screenPx <= 640;
    },
  },
  methods: {
    getSevLocation(product, related) {
      const city = product.city,
        regions = product.regions,
        subRegions = product.subRegions;

      if (!regions.length && !subRegions.length) {
        if (!city && related.extra_item_fields) {
          return related.extra_item_fields.city;
        }
        return city;
      }

      let sevLocation = regions && regions.length && regions[0].name;
      sevLocation += (subRegions && subRegions.length > 0) ? ` - ${ subRegions[0]?.name } (${ subRegions[0]?.code })` : `(${ regions[0]?.code })`;

      return sevLocation;
    },
    getNumPeopleText(people) {
      if (!people) {
        return;
      }

      const formattedNumPeopleText = people == '1' ? this.$t('for1PeopleFormatted') : this.$t('forXPeopleFormatted').replace('%s', people);
      return formattedNumPeopleText;
    },
    generateNewUrl,
    getTargetedPromotion,
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variables';
@import '../../scss/smartbox-icons';
@import '../../scss/typography.scss';

$old-price: #595959;
$gallery-grey: #ebebeb;
$nero: #1A203C;
$white: #ffffff;

$medium-up: 'only screen and (min-width: 768px)';
$large-up: 'only screen and (min-width: 1025px)';

* {
  box-sizing: border-box;
}

.product-vignette {
  color: $old-price;
  margin-bottom: 0;
  border: 1px solid $gallery-grey;
  border-left: 0;
  display: inline-block;
  text-decoration: none;
  word-break: break-word;

  &:nth-child(2n) {
    border-right: 0;
  }

  &:nth-child(n + 3) {
    border-top: 0;
  }

  .location__description {
    font-family: $brand-font-bold;
    color: $nero;
  }

  &:hover {
    .product-vignette__item {
      box-shadow: 0 rem-calc(6) rem-calc(12) 0 rgba(0, 0, 0, 0.25);
      cursor: pointer;
    }

    .product-vignette__description {
      color: $old-price;
    }

    .button--ghost {
      background-color: $brand-primary-color-1;
      color: $white;
    }
  }

  @media #{$medium-up} {
    border: 0;
    margin-bottom: 1rem;
  }

  &__item {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    padding: rem-calc(12);
    background: $white;
    height: 100%;

    @media #{$medium-up} {
      padding: 0;
      border-radius: rem-calc(5);
      border: 1px solid $gallery-grey;
    }
  }

  &__info {
    @media #{$medium-up} {
      flex: 1 0 auto;
      padding: 0 0.75rem;
    }
  }

  &__image {
    padding: 0;
    margin: 0;
    width: 100%;
    position: relative;

    @media #{$medium-up} {
      width: 100%;
      margin-right: 0;
      border-radius: 0;
    }

    > img {
      max-width: 100%;
      display: block;
      margin: 0 auto;
      border-top-left-radius: rem-calc(5);
      border-top-right-radius: rem-calc(5);
      width: 100%;
    }
  }

  &__title,
  &__info h5 {
    margin: 1rem 0 0;

    &:hover,
    &:focus {
      color: $brand-primary-color-1;
    }
  }

  &__title {
    font-size: rem-calc(14);
    line-height: rem-calc(18);

    @media #{$desktop-up} {
      font-size: rem-calc(16);
      line-height: rem-calc(20);
    }
  }

  &__clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media #{$medium-up} {
      -webkit-line-clamp: none;
      display: none;
    }
  }

  &__clamp3 {
    -webkit-line-clamp: 3;
  }

  &__clamp4 {
    -webkit-line-clamp: 4;
  }

  &__description {
    font-size: rem-calc(12);
    line-height: rem-calc(16);
    margin-top: rem-calc(8);
    margin-bottom: rem-calc(8);
    margin-left: 0;
    padding: 0;

    @media #{$medium-up} {
      margin-top: rem-calc(12);
      margin-bottom: rem-calc(12);
    }

    @media #{$desktop-up} {
      font-size: rem-calc(14);
      line-height: rem-calc(18);
    }

    strong {
      font-family: $brand-font-medium;
      font-weight: normal;
      color: $nero;
    }
  }

  &__description-icon {
    color: $old-price;
    font-size: rem-calc(28);
    line-height: rem-calc(17);
    margin-right: rem-calc(4);
  }

  &__description-item {
    display: flex;
    flex-direction: row;
    list-style: none;
    margin-bottom: rem-calc(12);
    font-size: rem-calc(12);
    line-height: rem-calc(16);
    color: $old-price;

    &:last-child {
      margin-bottom: 0;
    }

    @media #{$desktop-up} {
      font-size: rem-calc(14);
      line-height: rem-calc(18);
    }
  }

  &:hover {
    .product-vignette__description {
      color: $old-price;
    }
  }

  &__bottom {
    flex-direction: column;
    padding-top: 1rem;
    align-self: flex-start;
    margin-top: auto;

    @media #{$medium-up} {
      margin: 0;
      width: 100%;
      padding: 1rem 0.75rem;
      align-self: flex-end;
    }
  }

  .button--ghost {
    width: 100%;
    margin-top: 0.5rem;

    @media #{$medium-up} {
      margin-top: 1rem;
    }
  }

  &__price-wrapper {
    display: inline-flex;
    flex-direction: column;
    justify-content: flex-end;
    height: rem-calc(50);
  }

  &--promotion {
    .product-vignette__price-wrapper {
      height: rem-calc(76);
    }
  }
}
</style>
